import domready from 'domready';
import React, { useEffect, useState, Suspense, useCallback } from 'react';
import { render } from 'react-dom';
import { BrowserRouter as Router, useLocation, useHistory } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import _ from 'lodash';
import dayjs from 'dayjs';

/* ---------- Redux ---------- */
import { Provider, useSelector, useDispatch } from 'react-redux';
import { applyMiddleware as applyReduxMiddleware, createStore as createReduxStore } from 'redux';
import thunk from 'redux-thunk';

/* ---------- Matarial UI ---------- */
import { StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';

/* ---------- Apollo Graphql ---------- */
import { createHttpLink, ApolloClient, InMemoryCache, ApolloProvider, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { getCMSAPIUrl } from './utils/urlFactory';

import { alertReset, setStrapiToken } from './redux/stateActions';
import { fetchUser } from './redux/userAction';

import Modal from './components/Modal';

import Cookies from './components/Cookies';

import { StyledBackground } from './components/MainLayout/style';

import { Progress, Alert } from './components/index';

import Logger from './Logger';
import * as utils from './utils/utils';
import RoomClient from './RoomClient';
import reducers from './redux/reducers';
import theme from './theme/Theme';

import PublicRoute from './routes/publicRoute';
import PrivateRoute from './routes/privateRoute';

import { tokenDecrypt } from './services';

const logger = new Logger();
const reduxMiddlewares = [thunk];

// Workaround for environments where calling an unbound reference to `window.fetch`
// throws "Illegal invocation" (e.g. when libraries store `fetch` in a variable).
// Binding once up-front ensures `fetch(...)` always has the correct Window context.
if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.fetch.__boundToWindow) {
    const boundFetch = window.fetch.bind(window);
    boundFetch.__boundToWindow = true;
    window.fetch = boundFetch;
}

export const store = createReduxStore(
    reducers,
    undefined,
    applyReduxMiddleware(...reduxMiddlewares)
);

window.STORE = store;

RoomClient.init({ store });

domready(async () => {
    logger.debug('DOM ready');

    await utils.initialize();

    run();
});

/* ---------- Apollo Client ---------- */
const httpLink = createHttpLink({
    uri: `${getCMSAPIUrl()}/graphql`,
});

const authLink = setContext(async (_, { headers }) => {
    const strapiToken = store.getState().strapiToken;

    return {
        headers: {
            ...headers,
            authorization: `Bearer ${strapiToken}`,
        },
    };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.log(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );

            if (message === 'Invalid token.') {
                const role = localStorage.getItem('role');
                if (['Staff', 'Provider', 'Admin', 'Provider Admin'].includes(role)) {
                    window.location.replace('/provider/signin');
                } else {
                    window.location.replace('/signin');
                }
                localStorage.clear();
            }
        });
    if (networkError) console.log(`[Network error]: ${networkError}`);
});

const apolloClient = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
});

const AlertContainer = () => {
    const history = useHistory();
    const location = useLocation();

    const alert = useSelector((state) => state.alert);
    const user = useSelector((state) => state.user);
    const [show, setShow] = useState(false);

    const handleOnClick = useCallback(() => {
        const firstPath = location.pathname.split('/')[1];
        const secondPath = location.pathname.split('/')[2];

        if (user.patient && !['call', 'groupcall', 'opencall'].includes(firstPath)) {
            history.push('/notification');
            handleAlertClose();
        }
        if (user.staff && !['call', 'groupcall', 'opencall'].includes(secondPath)) {
            if (window.openNoti) {
                window.openNoti();
            }
            handleAlertClose();
        }
    }, [user, location, window]);

    const handleAlertClose = () => {
        setShow(false);
        store.dispatch(alertReset());
    };

    useEffect(() => {
        setShow(alert.show);
    }, [alert]);

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            <Alert
                severity={alert.severity}
                open={show}
                onClose={handleAlertClose}
                title={alert.title}
                onClick={handleOnClick}
            >
                {alert.content}
            </Alert>
        </div>
    );
};

const RouterContainer = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const user = useSelector((state) => state.user);

    const [isLogin, setIsLogin] = useState(false);
    const [isInit, setIsInit] = useState(false);
    const [initRoute, setInitRoute] = useState(null);

    useEffect(() => {
        if (!_.isEmpty(user)) {
            setIsLogin(true);
            setIsInit(true);
        }
    }, [user]);

    const handleClearToken = () => {
        localStorage.clear();
    };

    useEffect(() => {
        const verifyToken = async () => {
            const jwt = localStorage.getItem('stp_to');
            const role = localStorage.getItem('role');
            const hospital = localStorage.getItem('hospital');

            if (!_.isNull(jwt) && role && hospital) {
                try {
                    const path = location.pathname.split('/')[2];
                    let checkDuplicate = true;

                    if (['call', 'groupcall', 'opencall'].includes(path)) {
                        checkDuplicate = false;
                    }
                    const res = await tokenDecrypt({ token: jwt, role, checkDuplicate });

                    // check token expired date
                    if (dayjs(res.exp * 1000).isAfter(dayjs(), 'date')) {
                        dispatch(setStrapiToken(jwt));
                        dispatch(fetchUser(res.id, jwt, role, hospital));
                        setInitRoute(`${location.pathname}${location.search}`);
                    } else {
                        handleClearToken();
                    }
                } catch (error) {
                    console.log('error', error.response);
                    handleClearToken();
                    setIsInit(true);
                }
            } else {
                handleClearToken();
                setIsInit(true);
            }
        };

        verifyToken();
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            <StyledBackground />
            {isInit ? isLogin ? <PrivateRoute initRoute={initRoute} /> : <PublicRoute /> : <div />}
            <Cookies />
        </div>
    );
};

async function run() {
    render(
        <ApolloProvider client={apolloClient}>
            <Provider store={store}>
                <RecoilRoot>
                    <StylesProvider injectFirst>
                        <ThemeProvider theme={theme}>
                            <SCThemeProvider theme={theme}>
                                <MuiPickersUtilsProvider utils={DayjsUtils}>
                                    <Router>
                                        {/* <Modal /> */}
                                        <AlertContainer />
                                        <Suspense fallback={<Progress />}>
                                            <RouterContainer />
                                        </Suspense>
                                    </Router>
                                </MuiPickersUtilsProvider>
                            </SCThemeProvider>
                        </ThemeProvider>
                    </StylesProvider>
                </RecoilRoot>
            </Provider>
        </ApolloProvider>,

        document.getElementById('app-container')
    );
}
